<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Companies', path: {name: 'admin-companies'} }]" :name="loading ? 'Edit Company' : company.name" no-title/>
    <b-row v-if="!loading">
      <b-col lg="4" md="5">
        <admin-panel second-body with-footer>
          <div class="mt-3 text-center">
            <b-avatar
                v-if="company.logo_url"
                variant="primary"
                :src="company.logo_url"
                size="150"
            />
            <h4 class="card-title mt-3">{{ company.name }}</h4>
            <h6 class="card-subtitle">
              Created On {{ createdOn }}
              <template v-if="company.created_by">
                <br>by
                <b-link :to="{name: 'admin-user', params: {id: company.created_by.id.toString()}}">
                  {{ company.created_by.name }}
                </b-link>
              </template>
            </h6>
          </div>
          <template v-slot:second-body>
            <small class="text-muted">Current Tier Level</small>
            <h6>
              <star-rating :rating="company.tier_level"/>
            </h6>
            <small class="text-muted">Current Rating</small>
            <h6>
              <star-rating v-if="company.rating_level !== null" :rating="company.rating_level"/>
              <span v-else>New User</span>
            </h6>
            <small class="text-muted">Current Status</small>
            <h6>
              <admin-company-status-badge :company="company"/>
            </h6>
            <template v-if="approvedOn">
              <small class="text-muted">Approved</small>
              <h6>
                on {{ approvedOn }}
                <template v-if="company.approved_by">
                  <br>by
                  <b-link :to="{name: 'admin-user', params: {id: company.approved_by.id.toString()}}">
                    {{ company.approved_by.name }}
                  </b-link>
                </template>
              </h6>
            </template>
            <template v-else-if="rejectedOn">
              <small class="text-muted">Rejected</small>
              <h6>
                on {{ rejectedOn }}
                <template v-if="company.rejected_by">
                  <br>by
                  <b-link :to="{name: 'admin-user', params: {id: company.rejected_by.id.toString()}}">
                    {{ company.rejected_by.name }}
                  </b-link>
                </template>
              </h6>
            </template>
          </template>
          <template v-slot:footer>
            <div class="text-center mt-n1" v-if="company.status">
              <no-permission-wrapper v-if="company.status.code === 'active'" :show="!canUpdateCompanies">
                <b-btn
                    @click.prevent="deactivateCompany"
                    variant="danger"
                    class="mt-1"
                    :disabled="!canUpdateCompanies"
                >
                  Suspend Company
                </b-btn>
              </no-permission-wrapper>
              <template v-if="company.status.code === 'pending'">
                <no-permission-wrapper :show="!canUpdateCompanies">
                  <b-btn
                      @click="activateCompany"
                      variant="success"
                      class="mt-1"
                      :disabled="!canUpdateCompanies"
                  >
                    Activate Company
                  </b-btn>
                </no-permission-wrapper>
                <no-permission-wrapper :show="!canUpdateCompanies">
                  <b-btn
                      @click="rejectCompany"
                      variant="danger"
                      class="mt-1"
                      :disabled="!canUpdateCompanies"
                  >
                    Reject Company
                  </b-btn>
                </no-permission-wrapper>
              </template>
              <no-permission-wrapper v-else-if="company.status.code === 'suspended'" :show="!canUpdateCompanies">
                <b-btn
                    @click="activateCompany"
                    variant="success"
                    class="mt-1"
                    :disabled="!canUpdateCompanies"
                >
                  Activate Company
                </b-btn>
              </no-permission-wrapper>
            </div>
          </template>
        </admin-panel>
      </b-col>
      <b-col v-if="!isRejected" lg="8" md="7">
        <admin-panel sub-title="Edit Company Data">
          <b-form @submit.prevent="submitForm">
            <b-form-group label="Company Name">
              <b-form-input
                  v-model.trim="formData.name"
                  type="text"
                  :state="fieldValid('name')"
                  :disabled="!canUpdateCompanies"
              />
              <b-form-invalid-feedback :state="fieldValid('name')">
                {{ formValidation.name }}
              </b-form-invalid-feedback>
            </b-form-group>
<!--            <b-form-group label="Company EIN">-->
<!--              <b-form-input-->
<!--                  v-model.trim="formData.ein"-->
<!--                  type="text"-->
<!--                  :state="fieldValid('ein')"-->
<!--              />-->
<!--              <b-form-invalid-feedback :state="fieldValid('ein')">-->
<!--                {{ formValidation.ein }}-->
<!--              </b-form-invalid-feedback>-->
<!--            </b-form-group>-->
<!--            <b-form-group label="Multi Store Company">-->
<!--              <b-form-radio-group-->
<!--                v-model="formData.is_multi_store"-->
<!--                :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"-->
<!--                :state="fieldValid('is_multi_store')"-->
<!--              />-->
<!--              <b-form-invalid-feedback :state="fieldValid('is_multi_store')">-->
<!--                {{ formValidation.is_multi_store }}-->
<!--              </b-form-invalid-feedback>-->
<!--            </b-form-group>-->
            <no-permission-wrapper :show="!canUpdateCompanies">
              <b-button type="submit" variant="primary" :disabled="formState.processing === 'main' || !canUpdateCompanies">
                Update company Data
                <b-spinner v-if="formState.processing === 'main'" class="ml-1" small></b-spinner>
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>

        <admin-panel sub-title="Support Rep">
          <b-form @submit.prevent="submitSupportRep">
            <b-form-group label="Support Rep">
              <b-form-input
                  v-model.trim="repFormData.support_rep"
                  type="text"
                  :state="fieldValid('support_rep')"
                  :disabled="!canUpdateCompanies"
              />

              <b-form-invalid-feedback :state="fieldValid('support_rep')">
                {{ formValidation.support_rep }}
              </b-form-invalid-feedback>
            </b-form-group>

            <no-permission-wrapper :show="!canUpdateCompanies">
              <b-button
                  type="submit"
                  variant="primary"
                  :disabled="formState.processing === 'supportRep' || !canUpdateCompanies"
              >
                Update Support Rep
                <b-spinner v-if="formState.processing === 'supportRep'" class="ml-1" small />
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>

        <admin-panel sub-title="Sales Rep">
          <b-form @submit.prevent="submitSalesRep">
            <b-form-group label="Sales Rep">
              <b-form-input
                  v-model.trim="repFormData.sales_rep"
                  type="text"
                  :state="fieldValid('sales_rep')"
                  :disabled="!canUpdateCompanies"
              />

              <b-form-invalid-feedback :state="fieldValid('sales_rep')">
                {{ formValidation.sales_rep }}
              </b-form-invalid-feedback>
            </b-form-group>

            <no-permission-wrapper :show="!canUpdateCompanies">
              <b-button
                  type="submit"
                  variant="primary"
                  :disabled="formState.processing === 'salesRep' || !canUpdateCompanies"
              >
                Update Sales Rep
                <b-spinner v-if="formState.processing === 'salesRep'" class="ml-1" small />
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>

        <admin-panel sub-title="Company Tier Level">
          <b-form @submit.prevent="submitTierLevel">
            <b-form-group label="Tier Level">
              <b-form-select
                  v-model="tierFormData.tier_level"
                  :state="fieldValid('tier_level')"
                  :options="[1,2,3,4,5,6,7,8,9,10]"
                  :disabled="!canUpdateCompanies"
              />
              <b-form-invalid-feedback :state="fieldValid('tier_level')">
                {{ formValidation.tier_level }}
              </b-form-invalid-feedback>
            </b-form-group>
            <no-permission-wrapper :show="!canUpdateCompanies">
              <b-button type="submit" variant="primary" :disabled="formState.processing === 'tier' || !canUpdateCompanies">
                Update company Tier Level
                <b-spinner v-if="formState.processing === 'tier'" class="ml-1" small></b-spinner>
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>
        <admin-panel sub-title="Company Facilities">
          <b-overlay :show="licenses.loading">
            <admin-licenses-table
                v-if="canReadLicenses"
                :licenses="licenses.rows"
                with-actions
                @disapprove="disapproveLicense"
                @approve="requestApproveLicense"
                @resync="requestResyncLicense"
            />
            <div v-else class="text-danger font-medium">Sorry! You don't have permission to read licenses.</div>
          </b-overlay>
        </admin-panel>
        <admin-panel sub-title="External Integrations">
          <b-form @submit.prevent="submitLeafLink">
            <b-form-group label="LeafLink API Key">
              <b-form-input
                  v-model.trim="formData.leaflink_api_key"
                  type="text"
                  :state="fieldValid('leaflink_api_key')"
                  :disabled="!canUpdateCompanies"
              />
              Leave empty if you don't want to update it
              <b-form-invalid-feedback :state="fieldValid('leaflink_api_key')">
                {{ formValidation.leaflink_api_key }}
              </b-form-invalid-feedback>
            </b-form-group>
            <no-permission-wrapper :show="!canUpdateCompanies">
              <b-button type="submit" variant="primary" :disabled="formState.processing === 'leaflink' || !canUpdateCompanies">
                Update Leaflink Key
                <b-spinner v-if="formState.processing === 'leaflink'" class="ml-1" small></b-spinner>
              </b-button>
            </no-permission-wrapper>
          </b-form>
        </admin-panel>
      </b-col>
      <b-col cols="12">
        <admin-panel sub-title="Company Users">
          <b-overlay :show="users.loading">
            <admin-users-table v-if="canReadUsers" :users="users.rows"/>
            <div v-else class="text-danger font-medium">Sorry! You don't have permission to read users.</div>
          </b-overlay>

          <template v-slot:header-right>
            <no-permission-wrapper :show="!canCreateUsers">
              <b-button variant="success" @click.prevent="openNewUserModal" :disabled="!canCreateUsers">Add new Company User</b-button>
            </no-permission-wrapper>
          </template>
        </admin-panel>
      </b-col>
      <b-col cols="12">
        <admin-panel sub-title="Company Pricing Plans">
          <template v-if="canReadPricingPlans">
            <admin-pricing-plans-table
                v-if="company.pricing_plans.length"
                :plans="company.pricing_plans"
                @cancel="cancelPricingPlan"
                company-view
            />
            <h4 v-else class="mb-0">No pricing plans connected to this company</h4>
          </template>
          <div v-else class="text-danger font-medium">Sorry! You don't have permission to read pricing plans.</div>

          <template v-slot:header-right>
            <no-permission-wrapper :show="!canAssignPricingPlans">
              <b-btn variant="success" @click.prevent="openNewPricingPlanModal" :disabled="!canAssignPricingPlans">
                Assign new Pricing Plan
              </b-btn>
            </no-permission-wrapper>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
    <admin-license-approve-modal
        v-if="licenseToApprove"
        :license="licenseToApprove"
        @approve="approveLicense"
        @cancel="licenseToApprove = null"
        visible
    />
    <admin-license-resync-modal
        v-if="licenseToResync"
        :license="licenseToResync"
        @approve="resyncLicense"
        @cancel="licenseToResync = null"
        visible
    />
    <admin-new-user-modal
      v-if="company.id"
      ref="newUserModal"
      skip-admin-role
      :company-id="company.id"
      @created="onNewUserAdded"
    />
    <admin-new-company-pricing-plan-modal
        v-if="company.id"
        ref="newPricingPlanModal"
        :current-plans="company.pricing_plans"
        :company-id="company.id"
        @created="onNewPricingPlanAdded"
    />
  </b-container>
</template>

<script>
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import {convertFromUtc} from "../helpers";
import AdminCompanyStatusBadge from "../components/AdminCompanyStatusBadge";
import AdminLicensesTable from "../components/AdminLicensesTable";
import AdminLicenseApproveModal from "../components/AdminLicenseApproveModal";
import AdminUsersTable from "../components/AdminUsersTable";
import AdminNewUserModal from "../components/AdminNewUserModal";
import StarRating from "../components/StarRating";
import AdminPricingPlansTable from "../components/AdminPricingPlansTable.vue";
import AdminCompaniesTable from "../components/AdminCompaniesTable.vue";
import AdminNewCompanyPricingPlanModal from "../components/AdminNewCompanyPricingPlanModal.vue";
import AdminLicenseResyncModal from "../components/AdminLicenseResyncModal.vue";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "AdminCompany",
  components: {
    NoPermissionWrapper,
    AdminLicenseResyncModal,
    AdminNewCompanyPricingPlanModal,
    AdminCompaniesTable,
    AdminPricingPlansTable,
    StarRating,
    AdminNewUserModal,
    AdminUsersTable,
    AdminLicenseApproveModal,
    AdminLicensesTable,
    AdminCompanyStatusBadge,
    AdminPanel,
    AdminBreadcrumb,
    AdminPreloader
  },
  props: {
    id: String
  },
  data() {
    return {
      company: {},
      loading: true,
      formData: {},
      tierFormData: {},
      formValidation: {},
      formState: {
        processing: false
      },
      licenseTabIndex: 0,
      licenses: {
        loading: true,
        rows: []
      },
      users: {
        loading: true,
        rows: []
      },
      pricingPlans: [],
      licenseToApprove: null,
      licenseToResync: null,
      pollingResync: null,
      repFormData: {},
    }
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}`).then(({data}) => {
      if (data.company.status.code === 'pending') {
        //Pending companies should approved or rejected as applications.
        this.$router.replace({name: 'admin-application', params: {id: this.id}});
      } else {
        this.company = data.company;
      }
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-companies"});
    }).finally(() => {
      this.loading = false;
    });

    this.$api.get(`${API_ROUTES.ADMIN_LICENSES}`, {params: {company: this.id}}).then(({data}) => {
      this.licenses.rows = data.licenses;
    }).catch(({response}) => {

    }).finally(() => this.licenses.loading = false);

    this.$api.get(`${API_ROUTES.ADMIN_USERS}`, {params: {company: this.id}}).then(({data}) => {
      this.users.rows = data.users;
    }).catch(({response}) => {

    }).finally(() => this.users.loading = false);
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    fieldValid(field) {
      return typeof this.formValidation[field] !== "undefined" && this.formValidation[field].length > 0 ? false : null;
    },
    submitForm() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.formState.processing = 'main';
      this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}`, this.formData).then(({data}) => {
        this.company = {
          ...this.company,
          ...data.company
        };
        this.$bvToast.toast('Company information has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update company info', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    submitTierLevel() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.formState.processing = 'tier';
      this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}/tier`, this.tierFormData).then(({data}) => {
        this.company = {
          ...this.company,
          ...data.company
        };
        this.$bvToast.toast('Company tier level has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update company tier level', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    submitLeafLink() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.formState.processing = 'leaflink';
      this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}/leaflink`, this.formData).then(({data}) => {
        this.company = {
          ...this.company,
          ...data.company
        };
        this.$bvToast.toast('LeafLink API key has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });
        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update LeafLink API key', {
          title: 'Error!',
          variant: 'danger'
        });
        let validationErrors = {};
        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }
        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    activateCompany() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to activate company's access to GreenLists App", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}/activate`).then(({data}) => {
            this.company = {
              ...this.company,
              ...data.company
            };
            this.$bvToast.toast('Company has been successfully activated', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    deactivateCompany() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to suspend company's access to GreenLists App. This would also close all active company listings!", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}/suspend`).then(({data}) => {
            this.company = {
              ...this.company,
              ...data.company
            };
            this.$bvToast.toast('Company has been successfully suspended', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    rejectCompany() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to reject this company", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_COMPANIES}/${this.id}/reject`).then(({data}) => {
            this.company = {
              ...this.company,
              ...data.company
            };
            this.$bvToast.toast('Company has been successfully rejected', {
              title: 'Success!',
              variant: 'success'
            });
            this.formValidation = {};
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    requestApproveLicense(license) {
      this.licenseToApprove = license;
    },
    approveLicense(payload) {
      if (!this.checkPermission('canUpdateLicenses')) {
        return;
      }
      let license = payload.license,
          formData = {
            is_approved: true,
            is_to_buy: payload.is_to_buy,
            is_to_sell: payload.is_to_sell
          };
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_LICENSES}/${license.id}`, formData).then(({data}) => {
        let licenseIndex = this.licenses.rows.findIndex(l => l.id === license.id),
            licenses = [...this.licenses.rows];
        licenses[licenseIndex] = data.license;
        this.licenses.rows = licenses;
        this.$bvToast.toast('Facility has been successfully approved', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    disapproveLicense(license) {
      if (!this.checkPermission('canUpdateLicenses')) {
        return;
      }
      this.$bvModal.msgBoxConfirm("You are going to disapprove this facility", {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_LICENSES}/${license.id}`, {is_approved: false}).then(({data}) => {
            let licenseIndex = this.licenses.rows.findIndex(l => l.id === license.id),
              licenses = [...this.licenses.rows];
            licenses[licenseIndex] = data.license;
            this.licenses.rows = licenses;
            this.$bvToast.toast('Facility has been successfully disapproved', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to process operation', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    requestResyncLicense(payload) {
      this.pollingResync = payload.polling;
      this.licenseToResync = payload.license;
    },
    resyncLicense(daysToResync) {
      if (!this.checkPermission('canResyncLicenses')) {
        return;
      }
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_LICENSES}/${this.licenseToResync.id}/resync`, {
        days: daysToResync,
        polling: this.pollingResync
      }).then(({data}) => {
        let licenseIndex = this.licenses.rows.findIndex(l => l.id === this.licenseToResync.id),
            licenses = [...this.licenses.rows];
        licenses[licenseIndex] = data.license;
        this.licenses.rows = licenses;
        this.$bvToast.toast('Re-sync started', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    openNewUserModal() {
      if (!this.checkPermission('canCreateUsers')) {
        return;
      }
      this.$refs.newUserModal.open();
    },
    onNewUserAdded(user) {
      this.users.rows.push(user);
      this.$refs.newUserModal.close();
    },
    openNewPricingPlanModal() {
      if (!this.checkPermission('canAssignPricingPlans')) {
        return;
      }
      this.$refs.newPricingPlanModal.open();
    },
    onNewPricingPlanAdded(companyPlans) {
      this.company.pricing_plans = companyPlans;
      this.$refs.newPricingPlanModal.close();
    },
    cancelPricingPlan(plan) {
      if (!this.checkPermission('canCancelPricingPlans')) {
        return;
      }
      this.$bvModal.msgBoxConfirm(`You are going to cancel a "${plan.name}" pricing plan for this company`, {
        title: 'Please confirm',
        okVariant: 'success',
        centered: true
      }).then(value => {
        if (value) {
          this.loading = true;
          this.$api.post(`${API_ROUTES.ADMIN_PRICING_PLANS}/cancel`, {
            company: this.company.id,
            pricing_plan: plan.id
          }).then(({data}) => {
            this.company.pricing_plans = data.company_plans;
            this.$bvToast.toast('Pricing plan has been successfully cancelled', {
              title: 'Success!',
              variant: 'success'
            });
          }).catch(({response: {data}}) => {
            this.$bvToast.toast(data.message ?? 'Failed to cancel a pricing plan', {
              title: 'Error!',
              variant: 'danger'
            });
          }).finally(() => {
            this.loading = false;
          });
        }
      });
    },
    submitSupportRep() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }

      this.formState.processing = 'supportRep';

      this.$api.post(
          `${API_ROUTES.ADMIN_COMPANIES}/${this.id}/set-support-rep`,
          {
            support_rep: this.repFormData.support_rep
          }
      ).then(({data}) => {
        this.company = {
          ...this.company,
          ...data.company
        };

        this.$bvToast.toast('Support Rep has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });

        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update Support Rep', {
          title: 'Error!',
          variant: 'danger'
        });

        let validationErrors = {};

        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }

        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
    submitSalesRep() {
      if (!this.checkPermission('canUpdateCompanies')) {
        return;
      }

      this.formState.processing = 'salesRep';

      this.$api.post(
          `${API_ROUTES.ADMIN_COMPANIES}/${this.id}/set-sales-rep`,
          {
            sales_rep: this.repFormData.sales_rep
          }
      ).then(({data}) => {
        this.company = {
          ...this.company,
          ...data.company
        };

        this.$bvToast.toast('Sales Rep has been successfully updated', {
          title: 'Success!',
          variant: 'success'
        });

        this.formValidation = {};
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to update Sales Rep', {
          title: 'Error!',
          variant: 'danger'
        });

        let validationErrors = {};

        if (typeof data.errors !== 'undefined') {
          for (let fieldName in data.errors) {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          }
        }

        this.formValidation = validationErrors;
      }).finally(() => {
        this.formState.processing = false;
      });
    },
  },
  computed: {
    canUpdateCompanies() {
      return this.$store.getters['auth/hasAdminPermissions']('company_update');
    },
    canReadLicenses() {
      return this.$store.getters['auth/hasAdminPermissions']('license_read');
    },
    canUpdateLicenses() {
      return this.$store.getters['auth/hasAdminPermissions']('license_update');
    },
    canResyncLicenses() {
      return this.$store.getters['auth/hasAdminPermissions']('license_resync');
    },
    canReadUsers() {
      return this.$store.getters['auth/hasAdminPermissions']('user_read');
    },
    canCreateUsers() {
      return this.$store.getters['auth/hasAdminPermissions']('user_create');
    },
    canReadPricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_read');
    },
    canAssignPricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_assign');
    },
    canCancelPricingPlans() {
      return this.$store.getters['auth/hasAdminPermissions']('pricing_plan_assign');
    },
    createdOn() {
      if (!this.company.id) {
        return "";
      }
      return convertFromUtc(this.company.created_at, 'L');
    },
    approvedOn() {
      if (!this.company.approved_at) {
        return "";
      }
      return convertFromUtc(this.company.approved_at, 'L');
    },
    rejectedOn() {
      if (!this.company.rejected_at) {
        return "";
      }
      return convertFromUtc(this.company.rejected_at, 'L');
    },
    isRejected() {
      if (!this.company.id) {
        return false;
      }
      return this.company.status.code === 'rejected';
    }
  },
  watch: {
    company(company) {
      this.formData = {
        name: company.name,
        status: company.status.id,
        leaflink_api_key: company.leaflink_api_key
        //ein: company.ein,
        //is_multi_store: company.is_multi_store
      };
      this.tierFormData = {
        tier_level: company.tier_level
      };

      this.repFormData = {
        support_rep: company.support_rep,
        sales_rep: company.sales_rep
      };
    }
  }
}
</script>
